import React from "react";

import _ from "lodash";

import useForm from "commons/hooks/useForm";

import { FormHookType } from "commons/types/form";
import { ObjectPropertyType } from "commons/types/validation";

import { useBlockGroupContext } from "components/form/Blocks";
import { AttachmentType } from "components/ui/AttachmentPicker";

import { useSaveActionsContext } from "context/save";

import useSectionOneImageApi from "hooks/api/private/blocks/useSectionOneImageApi";
import useMobileOrDesktopFile from "hooks/useMobileOrDesktopFile";
import { ButtonDeleteInterface } from "hooks/useTitleFunctionsTopBlock";

import { SectionOneImageBody, SectionOneImageDetails } from "types/api/blocks/sectionOneImage";
import { DesktopMobileForm } from "types/api/commons";
import { DesktopOrMobile } from "types/enums";

import { converterAttachmentToFileWithPayload, createAttachmentType } from "utils/utils";
import { getExtraErrorsDescription, notEmptyMedia } from "utils/validators";

export type SectionOneImageForm = {
    id: string | undefined;
    img_id: string | undefined;
    img: DesktopMobileForm;
};

type SectionOneImageType = {
    setDesktopMobileImage: (fileToSet: AttachmentType, target: DesktopOrMobile) => void;
    buttonDelete: ButtonDeleteInterface;
    formHook: FormHookType<SectionOneImageForm>;
};

function useSectionOneImage(): SectionOneImageType {
    const { saveSectionOneImage, getOneSectionOneImage, deleteSectionOneImage } =
        useSectionOneImageApi();

    const { blockGroupCompositionId, blockGroupId, blockId, graphicalOrder } =
        useBlockGroupContext();

    const schema: ObjectPropertyType = React.useMemo(() => {
        return {
            type: "object",
            properties: {
                id: {
                    type: "simple",
                    validators: [],
                },
                img_id: {
                    type: "simple",
                    validators: [],
                },
                img: notEmptyMedia,
            },
        };
    }, []);

    const initialValues: SectionOneImageForm = React.useMemo(() => {
        return {
            id: undefined,
            img_id: undefined,
            img: {
                id: undefined,
                desktop: createAttachmentType({ id: null, label: "Desktop" }),
                mobile: createAttachmentType({ id: null, label: "Mobile" }),
            },
        };
    }, []);

    const formHook = useForm<SectionOneImageForm>(schema, initialValues, getExtraErrorsDescription);

    const { showAllError, setForm, isValid, form } = formHook;

    const { setDesktopMobileImage } = useMobileOrDesktopFile({
        setForm: setForm,
        path: "img",
    });

    const [sectionOneImageDetails, setSectionOneImageDetails] = React.useState<
        SectionOneImageDetails | undefined
    >(undefined);

    React.useEffect(() => {
        const [apiCall, abortController] = getOneSectionOneImage(
            { id: blockGroupCompositionId },
            { rejectNotFound: true }
        );

        apiCall.then(setSectionOneImageDetails).catch((error) => console.log(error));

        return () => abortController.abort();
    }, [setSectionOneImageDetails, getOneSectionOneImage, blockGroupCompositionId]);

    React.useEffect(() => {
        if (sectionOneImageDetails) {
            setForm((prevForm) => {
                const newForm = _.cloneDeep(prevForm);

                newForm.id = sectionOneImageDetails.id;
                newForm.img_id = sectionOneImageDetails.img_id;
                newForm.img = {
                    id: sectionOneImageDetails.img?.id,
                    desktop: createAttachmentType({
                        id: sectionOneImageDetails.img?.desktop.id,
                        label: "Desktop",
                        filename: sectionOneImageDetails.img?.desktop.filename,
                        status: 2,
                    }),
                    mobile: createAttachmentType({
                        id: sectionOneImageDetails.img?.mobile.id,
                        label: "Mobile",
                        filename: sectionOneImageDetails.img?.mobile.filename,
                        status: 2,
                    }),
                };

                return newForm;
            });
        }
    }, [setForm, sectionOneImageDetails]);

    const { addSaveAction } = useSaveActionsContext();

    React.useEffect(() => {
        addSaveAction(blockGroupCompositionId, () => {
            if (isValid) {
                const body: SectionOneImageBody = {
                    id: blockGroupCompositionId,
                    img: {
                        id: form.img.id,
                        desktop: converterAttachmentToFileWithPayload(form.img.desktop),
                        mobile: converterAttachmentToFileWithPayload(form.img.mobile),
                    },
                };

                const [apiCall] = saveSectionOneImage(body);

                const promises: Promise<unknown>[] = [];

                promises.push(apiCall);

                return Promise.all(promises);
            } else {
                showAllError();
                return Promise.reject("INVALID_FORM");
            }
        });
    }, [
        saveSectionOneImage,
        addSaveAction,
        showAllError,
        blockGroupCompositionId,
        isValid,
        form,
        blockGroupId,
        blockId,
        graphicalOrder,
    ]);

    return React.useMemo(
        (): SectionOneImageType => ({
            buttonDelete: deleteSectionOneImage,
            setDesktopMobileImage,
            formHook,
        }),
        [deleteSectionOneImage, setDesktopMobileImage, formHook]
    );
}

export default useSectionOneImage;
